import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Income() {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filters, setFilters] = useState({ category: 'none', startDate: '', endDate: '', product: '' });

  const [formData, setFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    category_id: '',
    subcategory_id: '',
    description: '',
    product_project: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [transRes, catRes] = await Promise.all([
        axios.get(`${API}/transactions`, {
          params: {
            type: 'income',
            category_id: filters.category && filters.category !== "none" ? filters.category : undefined,
            start_date: filters.startDate || undefined,
            end_date: filters.endDate || undefined,
            product_project: filters.product || undefined
          }
        }),
        axios.get(`${API}/categories`)
      ]);

      setTransactions(transRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.date || !formData.category_id) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        type: 'income',
        amount: parseFloat(formData.amount),
        subcategory_id: formData.subcategory_id || null,
        description: formData.description || null,
        product_project: formData.product_project || null
      };

      if (editingTransaction) {
        await axios.put(`${API}/transactions/${editingTransaction.id}`, payload);
        toast.success('Income updated');
      } else {
        await axios.post(`${API}/transactions`, payload);
        toast.success('Income added');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving transaction:', error);
      toast.error('Error saving');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this income?')) return;

    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast.success('Income deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Error deleting');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      amount: transaction.amount.toString(),
      date: new Date(transaction.date).toISOString().split('T')[0],
      category_id: transaction.category_id,
      subcategory_id: transaction.subcategory_id || '',
      description: transaction.description || '',
      product_project: transaction.product_project || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      date: new Date().toISOString().split('T')[0],
      category_id: '',
      subcategory_id: '',
      description: '',
      product_project: ''
    });
    setEditingTransaction(null);
  };

  const clearFilters = () => {
    setFilters({ category: 'none', startDate: '', endDate: '', product: '' });
  };

  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';

  const totalIncome = transactions.reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-primary">Income</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">Total: <span className="font-mono font-bold text-income">${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-md w-full sm:w-auto" data-testid="add-income-button">
              <Plus size={18} className="mr-2" />
              Add Income
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-card border-border mx-4">
            <DialogHeader>
              <DialogTitle className="text-primary">{editingTransaction ? 'Edit Income' : 'New Income'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="amount" className="text-foreground">Amount ($) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  data-testid="income-amount-input"
                  className="bg-background border-border text-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="date" className="text-foreground">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="income-date-input"
                  className="bg-background border-border text-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="category" className="text-foreground">Category *</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value, subcategory_id: '' })}>
                  <SelectTrigger data-testid="income-category-select" className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {parentCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id} className="text-foreground hover:bg-accent">{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.category_id && getSubcategories(formData.category_id).length > 0 && (
                <div>
                  <Label htmlFor="subcategory" className="text-foreground">Subcategory</Label>
                  <Select value={formData.subcategory_id} onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}>
                    <SelectTrigger data-testid="income-subcategory-select" className="bg-background border-border text-foreground">
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {getSubcategories(formData.category_id).map(cat => (
                        <SelectItem key={cat.id} value={cat.id} className="text-foreground hover:bg-accent">{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="product" className="text-foreground">Project/Product</Label>
                <Input
                  id="product"
                  value={formData.product_project}
                  onChange={(e) => setFormData({ ...formData, product_project: e.target.value })}
                  data-testid="income-product-input"
                  className="bg-background border-border text-foreground"
                  placeholder="e.g., Villa Project"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-foreground">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="income-description-input"
                  className="bg-background border-border text-foreground"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full" data-testid="income-submit-button">
                {editingTransaction ? 'Update' : 'Save'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-secondary border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={18} className="text-foreground" />
          <h3 className="font-bold text-sm text-primary">Filters</h3>
          {(filters.category || filters.startDate || filters.endDate || filters.product) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-button">
              <X size={16} className="mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="filter-category" className="text-foreground">Category</Label>
            <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
              <SelectTrigger id="filter-category" data-testid="filter-category-select" className="bg-background border-border text-foreground">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="none" className="text-foreground hover:bg-accent">All categories</SelectItem>
                {parentCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id} className="text-foreground hover:bg-accent">{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="filter-start" className="text-foreground">Start Date</Label>
            <Input
              id="filter-start"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              data-testid="filter-start-date"
              className="bg-background border-border text-foreground"
            />
          </div>
          <div>
            <Label htmlFor="filter-end" className="text-foreground">End Date</Label>
            <Input
              id="filter-end"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              data-testid="filter-end-date"
              className="bg-background border-border text-foreground"
            />
          </div>
          <div>
            <Label htmlFor="filter-product" className="text-foreground">Project/Product</Label>
            <Input
              id="filter-product"
              value={filters.product}
              onChange={(e) => setFilters({ ...filters, product: e.target.value })}
              data-testid="filter-product-input"
              className="bg-background border-border text-foreground"
              placeholder="Search..."
            />
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-2">No income yet</p>
            <p className="text-sm text-muted-foreground">Use the button above to add new income</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Category</th>
                  <th className="hidden md:table-cell text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Subcategory</th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Project/Product</th>
                  <th className="hidden sm:table-cell text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Description</th>
                  <th className="text-right py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Amount</th>
                  <th className="text-center py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((trans, index) => (
                  <tr key={trans.id} className="border-b border-border hover:bg-accent/50 transition-colors" data-testid={`income-row-${index}`}>
                    <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                      {new Date(trans.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-foreground">
                      <div className="max-w-[100px] sm:max-w-none truncate">{getCategoryName(trans.category_id)}</div>
                    </td>
                    <td className="hidden md:table-cell py-3 px-4 text-xs sm:text-sm text-foreground">
                      {trans.subcategory_id ? getCategoryName(trans.subcategory_id) : '-'}
                    </td>
                    <td className="hidden lg:table-cell py-3 px-4 text-xs sm:text-sm text-foreground">{trans.product_project || '-'}</td>
                    <td className="hidden sm:table-cell py-3 px-2 sm:px-4 text-xs sm:text-sm text-foreground">
                      <div className="max-w-[150px] lg:max-w-none truncate">{trans.description || '-'}</div>
                    </td>
                    <td className="py-3 px-2 sm:px-4 text-right font-mono font-semibold text-xs sm:text-sm text-income whitespace-nowrap">
                      ${trans.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-2 sm:px-4">
                      <div className="flex items-center justify-center gap-1 sm:gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(trans)}
                          data-testid={`edit-income-${index}`}
                          className="hover:bg-accent hover:text-primary"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(trans.id)}
                          data-testid={`delete-income-${index}`}
                          className="hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 size={16} className="text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}